import { MarkerClusterer } from "@googlemaps/markerclusterer";

function markerSvg(color, size, count) {
  const inner = size * 0.28;
  const outer = size * 0.44;
  const center = size / 2;
  const label = count
    ? `<text x="${center}" y="${center + inner * 0.4}" text-anchor="middle" dominant-baseline="middle"
        font-size="${inner * 0.9}" font-weight="700" font-family="sans-serif" fill="${color}">${count}</text>`
    : "";
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
    <circle cx="${center}" cy="${center}" r="${inner}" fill="${color}"/>
    <circle cx="${center}" cy="${center}" r="${outer}" fill="none" stroke="${color}" stroke-width="${size * 0.075}"/>
    ${label}
  </svg>`;
  return "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(svg);
}

function markerIcon(maps, color = "#000000", size = 36) {
  return {
    url: markerSvg(color, size, null),
    scaledSize: new maps.Size(size, size),
    anchor: new maps.Point(size / 2, size / 2),
  };
}

function tweenMarkerSize(maps, marker, fromSize, toSize, color, durationMs = 180) {
  let start = null;
  function step(ts) {
    if (!start) start = ts;
    const t = Math.min((ts - start) / durationMs, 1);
    const eased = t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t; // ease-in-out quad
    const size = fromSize + (toSize - fromSize) * eased;
    marker.setIcon(markerIcon(maps, color, size));
    if (t < 1) requestAnimationFrame(step);
  }
  requestAnimationFrame(step);
}

function clusterRenderer(maps, color) {
  return {
    render({ count, position }) {
      const size = count > 99 ? 52 : 44;
      return new maps.Marker({
        position,
        icon: {
          url: markerSvg(color, size, count),
          scaledSize: new maps.Size(size, size),
          anchor: new maps.Point(size / 2, size / 2),
        },
        zIndex: Number(maps.Marker.MAX_ZINDEX) + count,
      });
    },
  };
}

const STYLES_SIMPLE = [
  { elementType: "labels", stylers: [{ visibility: "off" }] },
  { elementType: "geometry", stylers: [{ color: "#e8e8e8" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#ffffff" }] },
  { featureType: "road", elementType: "geometry", stylers: [{ color: "#e8e8e8" }] },
];

const STYLES_DETAILED = [
  { elementType: "geometry", stylers: [{ saturation: -100 }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#616161" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#f5f5f5" }] },
  { featureType: "poi", stylers: [{ visibility: "off" }] },
  { featureType: "transit", stylers: [{ visibility: "off" }] },
  { featureType: "road", elementType: "geometry", stylers: [{ color: "#ffffff" }] },
  { featureType: "road", elementType: "labels", stylers: [{ visibility: "off" }] },
  { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#dadada" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#c9c9c9" }] },
];

let mapsApiPromise = null;

function loadGoogleMapsApi(apiKey) {
  if (mapsApiPromise) return mapsApiPromise;

  mapsApiPromise = new Promise((resolve, reject) => {
    if (window.google && window.google.maps) {
      resolve(window.google.maps);
      return;
    }

    window.__gmapsReady = () => {
      resolve(window.google.maps);
      delete window.__gmapsReady;
    };

    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&callback=__gmapsReady`;
    script.async = true;
    script.defer = true;
    script.onerror = () => reject(new Error("Google Maps failed to load"));
    document.head.appendChild(script);
  });

  return mapsApiPromise;
}

function getCssVar(name, fallback) {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim() || fallback;
}

function createMap(container, config) {
  const mapEl = container;
  const maps = window.google.maps;
  const markerColor = getCssVar("--color-secondary", "#BF0027");

  const map = new maps.Map(mapEl, {
    center: config.center,
    zoom: config.zoom,
    styles: config.mapStyle === "detailed" ? STYLES_DETAILED : STYLES_SIMPLE,
    disableDefaultUI: true,
    zoomControl: true,
    restriction: {
      latLngBounds: { north: 85, south: -85, west: -180, east: 180 },
      strictBounds: true,
    },
  });

  const bounds = new maps.LatLngBounds();
  let openInfoWindow = null;
  let activeMarker = null;

  const markers = config.markers.map((m, i) => {
    const position = { lat: m.lat, lng: m.lng };
    const marker = new maps.Marker({
      position,
      title: m.title,
      icon: markerIcon(maps, markerColor),
    });

    bounds.extend(position);

    marker.addListener("mouseover", () => {
      if (marker !== activeMarker) tweenMarkerSize(maps, marker, 36, 42, markerColor);
    });
    marker.addListener("mouseout", () => {
      if (marker !== activeMarker) tweenMarkerSize(maps, marker, 42, 36, markerColor);
    });

    if (m.infoHtml) {
      const infoWindow = new maps.InfoWindow({ content: m.infoHtml });

      marker.addListener("click", () => {
        if (openInfoWindow) openInfoWindow.close();
        if (activeMarker) activeMarker.setIcon(markerIcon(maps, markerColor));
        marker.setIcon(markerIcon(maps, "#BF0027", 42));
        activeMarker = marker;
        infoWindow.open(map, marker);
        openInfoWindow = infoWindow;
      });

      infoWindow.addListener("closeclick", () => {
        marker.setIcon(markerIcon(maps, markerColor));
        activeMarker = null;
      });

      if (m.autoOpen) {
        setTimeout(() => {
          if (openInfoWindow) openInfoWindow.close();
          if (activeMarker) activeMarker.setIcon(markerIcon(maps, markerColor));
          marker.setIcon(markerIcon(maps, "#BF0027", 42));
          activeMarker = marker;
          infoWindow.open(map, marker);
          openInfoWindow = infoWindow;
        }, 300);
      }
    }

    return marker;
  });

  if (config.cluster) {
    new MarkerClusterer({ map, markers, renderer: clusterRenderer(maps, markerColor) });
  } else {
    markers.forEach((marker) => marker.setMap(map));
  }

  if (config.fitBounds && config.markers.length > 1) {
    map.fitBounds(bounds, 60);
  } else if (config.fitBounds && config.markers.length === 1) {
    map.setCenter(bounds.getCenter());
    map.setZoom(12);
  }

  container._map = map;
  container._bounds = bounds;
  container._config = config;
}

function initContainer(container) {
  if (container._mapInitialized) return;
  container._mapInitialized = true;

  const raw = container.getAttribute("data-map-config");
  if (!raw) return;

  let config;
  try {
    config = JSON.parse(raw);
  } catch (e) {
    return;
  }

  const apiKey =
    config.apiKey ||
    (typeof ajax_object !== "undefined" && ajax_object.google_maps_api_key) ||
    "";
  if (!apiKey) return;

  loadGoogleMapsApi(apiKey).then(() => createMap(container, config));
}

function observeModals() {
  const modals = document.querySelectorAll(".c-modal");
  if (!modals.length) return;

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (
        mutation.type !== "attributes" ||
        mutation.attributeName !== "class"
      )
        return;

      const modal = mutation.target;
      if (!modal.classList.contains("is-modal-open")) return;

      const container = modal.querySelector("[data-venue-map]");
      if (!container) return;

      if (!container._mapInitialized) {
        initContainer(container);
      } else if (container._map) {
        window.google.maps.event.trigger(container._map, "resize");
        if (container._config.fitBounds && container._config.markers.length > 1) {
          container._map.fitBounds(container._bounds, 60);
        } else {
          container._map.setCenter(container._config.center);
          container._map.setZoom(container._config.zoom);
        }
      }
    });
  });

  modals.forEach((modal) => {
    observer.observe(modal, { attributes: true, attributeFilter: ["class"] });
  });
}

export function initVenueMap() {
  document.addEventListener("DOMContentLoaded", () => {
    const containers = document.querySelectorAll("[data-venue-map]");
    if (!containers.length) return;

    containers.forEach((container) => {
      if (!container.closest(".c-modal")) {
        initContainer(container);
      }
    });

    observeModals();
  });
}
